import { Injectable } from '@angular/core';
import { List } from 'immutable';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { BaseDataStore } from './base-data.store';
import { TestRunsApiService } from '../api/test-runs.api.service';
import { ITestRunCellEditEvent, ITestRunPatchPayload, ITestRunRowModel } from '../../models/test-runs/test-run-row.model';

@Injectable({
  providedIn: 'root',
})
export class TestRunsStore extends BaseDataStore {
  private _runs: BehaviorSubject<List<ITestRunRowModel>> = new BehaviorSubject(List<ITestRunRowModel>([]));
  private _isLoading: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private _lastError: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  public $runs: Observable<List<ITestRunRowModel>> = this._runs.asObservable();
  public $isLoading: Observable<boolean> = this._isLoading.asObservable();
  public $lastError: Observable<string | null> = this._lastError.asObservable();

  private _currentNodeId: string | null = null;

  constructor(private readonly testRunsApi: TestRunsApiService) {
    super();
    this.behaviorSubjectList.push(this._runs, this._isLoading, this._lastError);
  }

  public get currentNodeId(): string | null {
    return this._currentNodeId;
  }

  public loadRuns(nodeId: string, projectId: string, workspaceId: string | null): void {
    if (!nodeId) {
      this.clear();
      return;
    }
    this._currentNodeId = nodeId;
    this._isLoading.next(true);
    this._lastError.next(null);
    if (this.subscribers.loadRuns) {
      this.subscribers.loadRuns.unsubscribe();
    }
    this.subscribers.loadRuns = this.testRunsApi
      .getTestRuns(nodeId, projectId, workspaceId)
      .pipe(
        tap(() => this._isLoading.next(false)),
        catchError((err) => {
          this._isLoading.next(false);
          this._lastError.next(this.formatError(err));
          return of([] as ITestRunRowModel[]);
        }),
      )
      .subscribe((rows: ITestRunRowModel[]) => {
        if (this._currentNodeId !== nodeId) {
          return;
        }
        this._runs.next(List<ITestRunRowModel>(rows ?? []));
      });
  }

  public clear(): void {
    this._currentNodeId = null;
    this._runs.next(List<ITestRunRowModel>([]));
    this._isLoading.next(false);
    this._lastError.next(null);
  }

  public patchRun(event: ITestRunCellEditEvent): Observable<ITestRunRowModel | null> {
    const row = event?.row;
    if (!row || !row.labelId || !row.nodeId || !event?.fieldName) {
      return of(null);
    }
    if (row.rowKind !== 'label-run' && row.rowKind !== 'related-bug') {
      return of(null);
    }
    const payload: ITestRunPatchPayload = {
      fieldId: event.fieldId,
      fieldName: event.fieldName,
      value: event.value,
    };

    // Optimistic local update: touch only the edited field so concurrent
    // edits to other fields on the same row aren't discarded.
    const current = this._runs.getValue();
    const idx = current.findIndex((r) => r?.rowId === row.rowId);
    if (idx !== -1) {
      const currentRow = current.get(idx);
      if (currentRow) {
        this._runs.next(
          current.set(idx, {
            ...currentRow,
            fields: { ...currentRow.fields, [event.fieldId]: event.value },
          }),
        );
      }
    }

    return this.testRunsApi.patchTestRun(row.nodeId, row.labelId, payload).pipe(
      tap((fresh) => {
        if (!fresh) {
          return;
        }
        const now = this._runs.getValue();
        const nextIdx = now.findIndex((r) => r?.rowId === fresh.rowId);
        if (nextIdx !== -1) {
          this._runs.next(now.set(nextIdx, fresh));
        }
      }),
      catchError((err) => {
        this._lastError.next(this.formatError(err));
        // Roll back only the single field we optimistically wrote.
        const rollbackList = this._runs.getValue();
        const rollbackIdx = rollbackList.findIndex((r) => r?.rowId === row.rowId);
        if (rollbackIdx !== -1) {
          const rollbackRow = rollbackList.get(rollbackIdx);
          if (rollbackRow) {
            this._runs.next(
              rollbackList.set(rollbackIdx, {
                ...rollbackRow,
                fields: { ...rollbackRow.fields, [event.fieldId]: event.previousValue },
              }),
            );
          }
        }
        return throwError(() => err);
      }),
    );
  }

  private formatError(err: unknown): string {
    if (!err) return 'Unknown error';
    if (typeof err === 'string') return err;
    return (err as { message?: string; statusText?: string }).message ?? (err as { statusText?: string }).statusText ?? 'Request failed';
  }
}
